import { ReadonlyVec3, vec4 } from 'gl-matrix';
import { ShaderProgram } from '~/engine/core/utils/ShaderProgram';
import { AdaptiveSphere } from '~/engine/data/common/customs/AdaptiveSphere';
import { BrushStamp, BrushStampContext, LayerDrawer } from './BrushStamp';

/**
 * {@link BrushStamp} of the adaptive brush: the part of the brush disc, or
 * of the brush ball for a 3D stroke, that an {@link AdaptiveSphere}
 * flood-fills from the cursor through similar image intensities.
 */
export class AdaptiveBrushStamp implements BrushStamp {
    public readonly label = 'Adaptive brush';

    private readonly shader = new AdaptiveBrushDrawShader();

    private sphere: AdaptiveSphere | undefined;

    public constructor(private readonly context: BrushStampContext) {}

    public stampAt(position: ReadonlyVec3, draw: LayerDrawer): void {
        const sphere = this.sphereAt(position);
        sphere.bindReadTexture();
        this.shader.start();
        try {
            draw(sphere.grid);
        } finally {
            this.shader.stop();
        }
    }

    public delete(): void {
        this.sphere?.delete();
        this.shader.delete();
    }

    /** The sphere, filled from `position`. */
    private sphereAt(position: ReadonlyVec3): AdaptiveSphere {
        if (this.sphere) {
            this.sphere.setPosition(position);
            return this.sphere;
        }
        const { view, target, image, diameter, margin, strokeMode } =
            this.context;
        this.sphere = new AdaptiveSphere(
            image,
            position,
            diameter,
            view.rotation,
            strokeMode,
            vec4.fromValues(
                target.color[0],
                target.color[1],
                target.color[2],
                1
            ),
            margin
        );
        return this.sphere;
    }
}

/**
 * Shader that is used to render texture content as-is: to copy an {@link AdaptiveSphere}'s SDF into a brush stroke's draw buffer.
 */
class AdaptiveBrushDrawShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;
    in vec3 textureCoords;

    out vec3 pass_textureCoordinates;

    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoordinates = textureCoords;
    }
    `;

    /**
     * Fragment shader.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;

    out float color;

    uniform sampler3D sampler;

    void main(){
        color = texture(sampler, pass_textureCoordinates).r;
    }
    `;

    constructor() {
        super(
            AdaptiveBrushDrawShader.VERTEX_CONTENT,
            AdaptiveBrushDrawShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        super.storeUniformLocations();
    }
}
